import colors from "assets/colors";
import { Chart, registerables } from "chart.js";
import BoxContainer from "components/BoxContainer";
import NoData from "components/NoData";
import PageContainer from "components/PageContainer";
import {
  ANALYSIS,
  Analysis,
  ANALYSIS_COLOR_KEYS,
  UserAnalysis,
} from "containers/dashboard/types";
import { StudentInfoState } from "global-states";
import { useLocationParams } from "hooks/location";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import { Button, Divider, Grid, Header, Loader } from "semantic-ui-react";
import { mapLabels } from "util/label";
import usePerformance from "./performance-hooks";
import { PerformancePayload } from "./types";

const HeaderStyle: CSSProperties = {
  color: colors.darkBlue,
  padding: "1em",
  margin: 0,
};

const DividerStyle: CSSProperties = {
  margin: 0,
};

const GraphContainerStyle: CSSProperties = {
  padding: "1em",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
};

const StudentInfoSpanStyle: CSSProperties = {
  fontWeight: 600,
  marginRight: "1em",
};

const ErrorInfoSpanStyle: CSSProperties = {
  color: "red",
  padding: "1em",
};

const ChartContainerStyle: CSSProperties = {
  minHeight: 500,
  height: 600,
  position: "relative",
};

const ButtonGroupStyle: CSSProperties = {
  width: "100%",
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: "1em",
};

const PerformancePage = () => {
  const student = useRecoilValue(StudentInfoState);
  const [benchmark, setBenchmark] = useState<"school" | "class">("school");
  const { data, error, isLoading } = usePerformance();

  const [analysisFilter, setAnalysisFilter] = useState<ANALYSIS[]>([
    ANALYSIS.LQ,
    ANALYSIS.UQ,
    ANALYSIS.MEDIAN,
  ]);

  useLocationParams();

  function onSetBenchmark(benchmark: "school" | "class") {
    setBenchmark(benchmark);
  }

  const dataExists = useMemo(() => {
    return Object.values(
      data?.data.results[benchmark] || data?.data.pred[benchmark] || {}
    ).some(
      (val) =>
        Object.keys(val).length > 0 &&
        Object.values(val).some((val) => val !== -1)
    );
  }, [benchmark, data?.data.pred, data?.data.results]);

  return (
    <PageContainer title="本學年表現" showDropdown>
      <Grid columns={1}>
        <Grid.Column>
          <BoxContainer>
            <Header content="本學年表現" style={HeaderStyle} />
            <Divider style={DividerStyle} />
            <div style={GraphContainerStyle}>
              <div>
                <span style={StudentInfoSpanStyle}>
                  班別: {student?.class_name}
                </span>
                <span style={StudentInfoSpanStyle}>
                  學生: {student?.student_name}
                </span>
              </div>
            </div>
            <div style={ErrorInfoSpanStyle}>{error}</div>
            <div style={ButtonGroupStyle}>
              <Button.Group>
                <Button
                  style={{
                    backgroundColor:
                      benchmark === "school" ? "gray" : "lightgray",
                  }}
                  content="學校"
                  onClick={() => onSetBenchmark("school")}
                />
                <Button
                  style={{
                    backgroundColor:
                      benchmark === "class" ? "gray" : "lightgray",
                  }}
                  content="班別"
                  onClick={() => onSetBenchmark("class")}
                />
              </Button.Group>
            </div>
            <div style={ButtonGroupStyle}>
              {Object.values(ANALYSIS).map((v, i) => (
                <Button
                  key={i}
                  content={v}
                  onClick={() =>
                    setAnalysisFilter((prev) =>
                      prev.includes(v)
                        ? prev.filter((k) => k !== v)
                        : prev.concat(v)
                    )
                  }
                  style={{
                    backgroundColor: analysisFilter.includes(v)
                      ? "lightgray"
                      : ANALYSIS_COLOR_KEYS[v as keyof Analysis],
                  }}
                />
              ))}
            </div>
            <div style={ChartContainerStyle}>
              <Loader active={isLoading} />
              {dataExists ? (
                <PredictionsGraph
                  data={data}
                  display={benchmark}
                  analysisFilter={analysisFilter}
                />
              ) : (
                <NoData isLoading={isLoading} />
              )}
            </div>
          </BoxContainer>
        </Grid.Column>
      </Grid>
    </PageContainer>
  );
};

const PredictionsGraph = ({
  data,
  display,
  analysisFilter,
}: {
  data?: PerformancePayload;
  display: "school" | "class";
  analysisFilter: ANALYSIS[];
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (chartRef && chartRef.current && data) {
      Chart.register(...registerables);
      const dateKeys = Object.keys(data.data.results[display].平均).concat(
        Object.keys(data.data.pred[display]?.平均 || {})
      );
      const datasets = Object.entries(data.data.results[display])
        .filter((v) => !analysisFilter.includes(v[0] as ANALYSIS))
        .map(([key, value], i) => {
          return {
            label: mapLabels(key as keyof (Analysis | UserAnalysis)),
            data: Object.values(value),
            fill: ANALYSIS_COLOR_KEYS[key as keyof Analysis],
            borderColor: ANALYSIS_COLOR_KEYS[key as keyof Analysis],
            tension: 0.1,
          };
        })
        .concat(
          Object.entries(data.data.pred[display])
            .filter((v) => !analysisFilter.includes(v[0] as ANALYSIS))
            .map(([key, value], i) => {
              const keys = Object.keys(
                data.data.results[display][key as keyof Analysis]
              );
              const lastPoint =
                data.data.results[display][key as keyof Analysis][
                  keys[keys.length - 1]
                ];
              return {
                label: `${mapLabels(
                  key as keyof (Analysis | UserAnalysis)
                )}(預測)`,
                data: [
                  ...new Array(
                    Object.keys(
                      data.data.results[display][key as keyof Analysis]
                    ).length - 1
                  ),
                ]
                  .map((_) => NaN)
                  .concat([lastPoint, ...Object.values(value)]),
                fill: ANALYSIS_COLOR_KEYS[key as keyof Analysis],
                borderColor: ANALYSIS_COLOR_KEYS[key as keyof Analysis],
                tension: 0.1,
                borderDash: [10, 5],
              };
            })
        );

      const chart = new Chart(chartRef.current, {
        type: "line",
        data: {
          labels: dateKeys,
          datasets: datasets,
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  if (
                    ctx.dataIndex !== ctx.dataset.data.length - 1 &&
                    ctx.dataset.label?.includes("預測")
                  ) {
                    return "";
                  }
                  return `${ctx.dataset.label}: ${ctx.formattedValue}`;
                },
              },
            },
          },
          scales: {
            y: {
              title: {
                display: true,
                text: "成績 (%)",
              },
            },
          },
          responsive: true,
          maintainAspectRatio: false,
        },
      });
      return () => {
        chart.destroy();
      };
    }
  }, [analysisFilter, data, display]);
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
        padding: "1em",
      }}
    >
      <canvas ref={chartRef} />
    </div>
  );
};

export default PerformancePage;
